import * as React from 'react';
import { Menu as BaseMenu } from '@base-ui-components/react/menu';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { PopupCloseProvider, useCloseWhenAnchorHidden, usePopupOpen } from '@/shared/lib/popupAnchor';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './Menu.module.css';

/**
 * DropdownMenu поверх Base UI. Использование:
 *
 *   <Menu>
 *     <MenuTrigger render={<Button variant="ghost" />}>...</MenuTrigger>
 *     <MenuContent>
 *       <MenuItem onClick={...}>Действие</MenuItem>
 *       <MenuSeparator />
 *       <MenuItem disabled>Недоступно</MenuItem>
 *       <MenuItem variant="danger">Удалить</MenuItem>
 *     </MenuContent>
 *   </Menu>
 *
 * @use выпадающее меню по клику на кнопку.
 */

export function Menu(props: React.ComponentProps<typeof BaseMenu.Root>) {
  const { open, onOpenChange, close } = usePopupOpen(props);
  return (
    <PopupCloseProvider value={close}>
      <BaseMenu.Root {...props} open={open} onOpenChange={onOpenChange} />
    </PopupCloseProvider>
  );
}
export const MenuTrigger = BaseMenu.Trigger;

type PositionerProps = React.ComponentProps<typeof BaseMenu.Positioner>;

const SubmenuContext = React.createContext(false);
const SUBMENU_COLLISION_AVOIDANCE = { fallbackAxisSide: 'end' } as const;

export const MenuContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.Popup> &
    Pick<PositionerProps, 'align' | 'side' | 'sideOffset'>
>(function MenuContent({ className, children, align, side, sideOffset = 6, ...rest }, ref) {
  const isSubmenu = React.useContext(SubmenuContext);
  const positionerRef = useCloseWhenAnchorHidden();
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        collisionAvoidance={isSubmenu ? SUBMENU_COLLISION_AVOIDANCE : undefined}
        className={styles.positioner}
        ref={positionerRef}
      >
        <BaseMenu.Popup ref={ref} className={cn(styles.popup, isSubmenu && styles.subPopup, className)} {...rest}>
          <Scroller className={styles.scroller} viewportClassName={styles.viewport}>
            {children}
          </Scroller>
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
});

export const MenuGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.Group>
>(function MenuGroup({ className, ...rest }, ref) {
  return <BaseMenu.Group ref={ref} className={cn(styles.group, className)} {...rest} />;
});

export const MenuGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.GroupLabel>
>(function MenuGroupLabel({ className, ...rest }, ref) {
  return <BaseMenu.GroupLabel ref={ref} className={cn(styles.groupLabel, className)} {...rest} />;
});

export const MenuItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.Item> & { variant?: 'default' | 'danger' }
>(function MenuItem({ className, variant = 'default', ...rest }, ref) {
  return (
    <BaseMenu.Item
      ref={ref}
      className={cn(styles.item, variant === 'danger' && styles.danger, className)}
      {...rest}
    />
  );
});

export function MenuSub(props: React.ComponentProps<typeof BaseMenu.SubmenuRoot>) {
  return (
    <SubmenuContext.Provider value>
      <BaseMenu.SubmenuRoot {...props} />
    </SubmenuContext.Provider>
  );
}

export const MenuSubTrigger = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.SubmenuTrigger>
>(function MenuSubTrigger({ className, children, ...rest }, ref) {
  return (
    <BaseMenu.SubmenuTrigger ref={ref} className={cn(styles.item, styles.subTrigger, className)} {...rest}>
      {children}
      <ChevronRight size={14} aria-hidden className={styles.subChevron} />
    </BaseMenu.SubmenuTrigger>
  );
});

export const MenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof BaseMenu.Separator>
>(function MenuSeparator({ className, ...rest }, ref) {
  return <BaseMenu.Separator ref={ref} className={cn(styles.separator, className)} {...rest} />;
});
